import React, { useState } from "react";
import { ArrowRight, Mail, Loader2, ExternalLink } from "lucide-react";
import GoogleIcon from "@/components/GoogleIcon";
import ChaosLogo from "@/components/ChaosLogo";
import { AuthAPI } from "@/services/api";

/**
 * Unified email + Google auth card.
 *
 * - Google button: redirects to Emergent Auth provider.
 *   REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 *   THIS BREAKS THE AUTH.
 * - Email button: requests a magic link. In this dev phase the backend
 *   returns the link inline (no email service yet); we surface it as a
 *   one-click button the operator can follow.
 */
const AuthCard = ({ variant = "inline" }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(null); // 'email' | 'google' | null
  const [magicLink, setMagicLink] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!validEmail || loading) return;
    setErrorMsg("");
    setMagicLink(null);
    setLoading("email");
    try {
      const { magic_link } = await AuthAPI.requestEmail(email.trim());
      setMagicLink(magic_link);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.detail || "Could not generate magic link."
      );
    } finally {
      setLoading(null);
    }
  };

  const handleGoogle = () => {
    if (loading) return;
    setLoading("google");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    const redirectUrl = `${window.location.origin}/`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  const isModal = variant === "modal";

  return (
    <div
      data-testid="auth-card"
      className={`w-full bg-[#020617]/95 border border-white/[0.08] text-[#f1f5f9] ${
        isModal ? "p-7 sm:p-8" : "p-6 sm:p-8"
      }`}
    >
      <div className="flex items-center gap-3 mb-7">
        <ChaosLogo size={36} />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#94a3b8]">
          <div className="text-[#f1f5f9]">/AUTH NODE</div>
          <div className="text-[#FF0033]">ALPHA ACCESS LAYER</div>
        </div>
      </div>

      {(() => {
        if (typeof window === "undefined") return null;
        let reason = "";
        try {
          reason = window.localStorage.getItem("cmh-auth-reason") || "";
        } catch {
          return null;
        }
        if (!reason) return null;
        return (
          <div
            data-testid="auth-reason-banner"
            className="mb-6 border border-[#FF0033]/40 bg-[#FF0033]/[0.08] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#FF0033]"
          >
            {reason}
          </div>
        );
      })()}

      <h2
        data-testid="auth-headline"
        className="text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-[#f1f5f9] leading-[1.05] mb-2"
      >
        Initialize <span className="text-[#FF0033]">access</span> credentials.
      </h2>
      <p className="font-mono text-xs text-[#94a3b8] mb-7 leading-relaxed">
        Authenticate to deploy the truth-scan terminal. Two credentials types
        accepted on this node.
      </p>

      <form
        onSubmit={handleEmail}
        className="flex flex-col gap-3"
        data-testid="email-form"
      >
        <label
          htmlFor="auth-email"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#64748b]"
        >
          Operator email
        </label>
        <div className="relative">
          <Mail
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569] pointer-events-none"
          />
          <input
            id="auth-email"
            data-testid="email-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="operator@chaos.hub"
            className="w-full bg-[#0b0f19] border border-white/[0.08] text-[#f1f5f9] placeholder:text-[#334155] font-mono text-sm pl-9 pr-3 py-3 focus:outline-none focus:border-[#FF0033] transition-colors"
          />
        </div>

        <button
          type="submit"
          data-testid="email-submit-button"
          disabled={!validEmail || loading !== null}
          className="mt-1 group inline-flex items-center justify-center gap-2 bg-[#FF0033] hover:bg-white hover:text-black disabled:bg-[#1e293b] disabled:text-[#475569] disabled:cursor-not-allowed text-white font-bold uppercase tracking-[0.18em] px-5 py-3 text-xs sm:text-sm border border-transparent hover:border-white transition-colors duration-200 cursor-pointer"
        >
          {loading === "email" ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Generating link...
            </>
          ) : (
            <>
              Continue with Email
              <ArrowRight
                size={14}
                className="transition-transform group-hover:translate-x-1"
              />
            </>
          )}
        </button>
      </form>

      {magicLink && (
        <a
          href={magicLink}
          data-testid="magic-link-button"
          className="mt-4 group flex items-center gap-3 border border-[#FF0033]/40 bg-[#FF0033]/[0.08] hover:bg-[#FF0033] hover:text-white text-[#f1f5f9] px-4 py-3 transition-colors"
        >
          <ExternalLink size={14} className="text-[#FF0033] group-hover:text-white shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF0033] group-hover:text-white">
              Dev mode — email service offline
            </p>
            <p className="text-[13px] font-semibold truncate">
              Open magic link to verify {email.trim()} →
            </p>
          </div>
        </a>
      )}

      {errorMsg && (
        <p
          data-testid="auth-error"
          className="mt-3 font-mono text-[11px] text-[#FF0033]"
        >
          {errorMsg}
        </p>
      )}

      <div className="flex items-center gap-3 my-6">
        <span className="flex-1 h-px bg-white/[0.08]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#475569]">
          OR
        </span>
        <span className="flex-1 h-px bg-white/[0.08]" />
      </div>

      <button
        type="button"
        onClick={handleGoogle}
        disabled={loading !== null}
        data-testid="google-signin-button"
        className="w-full inline-flex items-center justify-center gap-3 bg-[#f1f5f9] hover:bg-white text-[#0b0f19] font-semibold tracking-tight px-5 py-3 text-sm border border-[#f1f5f9] hover:border-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer"
      >
        {loading === "google" ? (
          <>
            <Loader2 size={16} className="animate-spin text-[#0b0f19]" />
            <span>Redirecting to Google...</span>
          </>
        ) : (
          <>
            <GoogleIcon size={18} />
            <span>Sign in with Google</span>
          </>
        )}
      </button>

      <p className="mt-6 font-mono text-[10px] leading-relaxed uppercase tracking-[0.18em] text-[#475569]">
        By continuing you consent to CMH diagnostic logging. New
        New operators receive 7 starter credits on first activation.
      </p>
    </div>
  );
};

export default AuthCard;
